import type { CustomRoleTaxonomySelection } from '@revelio/data-access';

import type { CustomActivityTaxonomySelection } from '../../../next/CustomDash/filters/definitions/activityTaxonomy';
import type { CustomSkillTaxonomySelection } from '../../../next/CustomDash/filters/definitions/skillTaxonomy';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  type SingleValueFilter,
  type StaticListFilter,
} from '../../../next/CustomDash/filters/types/filters';
import {
  PreferenceScope,
  UserPreferenceKey,
  UserPreferenceSubKey,
  type UserPreferences,
} from '../types';
import { resolveGeographyPreference } from './geography-preference';
import {
  CONTINGENT_ONLY_SELECTION_ID,
  resolveWorkforceSegment,
  workforceSegmentToSelection,
} from './workforce-segment';

type FilterUpdate = Partial<Filter> & { id: FilterId };

type PreferenceFilterAdapter<K extends UserPreferenceKey> = {
  toPreference: (
    activeFilterMap: FilterSelectionsMap,
    affectedScope: PreferenceScope
  ) => Partial<UserPreferences>;
  toFilter?: (
    value: UserPreferences[K],
    subKey?: UserPreferenceSubKey
  ) => FilterUpdate[];
  relevantFilters?: FilterId[];
};

export const preferenceAdapters: Partial<
  Record<UserPreferenceKey, PreferenceFilterAdapter<UserPreferenceKey>>
> = {
  [UserPreferenceKey.Geography]: {
    relevantFilters: [FilterId.GEOGRAPHY_TAXONOMY],
    // Deliberately emits nothing: geography is the one preference whose value
    // is authored by the two pickers, never by page filter state.
    // `preferencesFromFilters` OVERRIDES the stored preference in the
    // preferences modal, so any value returned here becomes what the modal
    // shows and what the next save writes. Since this filter is serialized
    // into every URL, a reload replays it and it cannot be told apart from a
    // shared link — so letting it speak for the preference lets a stale URL
    // silently overwrite a stored one (it did: a legacy `'administrative'` row
    // resolved back to metro). The filter carries the taxonomy for RENDERING;
    // the preference stays the pickers' to write.
    toPreference: () => ({}),
    // Rows written before the taxonomy migration still hold the string modes
    // `'msa'` / `'administrative'`, so the raw preference goes through the
    // hardened resolver rather than a cast.
    // Deliberately NOT `isUserSubmitted` — this direction is a sync, not a
    // choice. `affectedScope` changes as the primary filter resolves, so this
    // can first run against defaults; marking it user-submitted would make that
    // premature value permanently authoritative, because the gate this very
    // adapter feeds (`isPreferenceUserSubmitted`) would then skip every later
    // correction. Only a link marks the filter — see `validateGeographyFilters`.
    toFilter: (value) => {
      const { taxonomyId } = resolveGeographyPreference(value);
      if (!taxonomyId) return [];
      return [
        {
          id: FilterId.GEOGRAPHY_TAXONOMY,
          type: FilterType.VALUE,
          value: { taxonomyId },
        },
      ];
    },
  },
  [UserPreferenceKey.ContingentWorkers]: {
    relevantFilters: [FilterId.CONTINGENT_WORKERS],
    toPreference: (activeFilterMap) => {
      const filter = activeFilterMap.get(FilterId.CONTINGENT_WORKERS);
      const selections = (filter as StaticListFilter)?.selections ?? [];
      if (!selections.length) {
        return {};
      }
      if (selections.some((s) => s.id === CONTINGENT_ONLY_SELECTION_ID)) {
        return { [UserPreferenceKey.ContingentWorkers]: 'contingentOnly' };
      }
      if (selections.some((s) => s.id === 'true')) {
        return { [UserPreferenceKey.ContingentWorkers]: true };
      }
      return { [UserPreferenceKey.ContingentWorkers]: false };
    },
    toFilter: (value) => [
      {
        id: FilterId.CONTINGENT_WORKERS,
        type: FilterType.STATIC_LIST,
        selections: [
          workforceSegmentToSelection(resolveWorkforceSegment(value)),
        ],
        isUserSubmitted: true,
        isVisible: false,
      },
    ],
  },
  [UserPreferenceKey.Taxonomies]: {
    relevantFilters: [
      FilterId.ROLE_TAXONOMY,
      FilterId.SKILL_TAXONOMY,
      FilterId.ACTIVITY_TAXONOMY,
    ],
    toPreference: (activeFilterMap, affectedScope) => {
      const roleFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY) as
        | SingleValueFilter<CustomRoleTaxonomySelection>
        | undefined;
      const skillFilter = activeFilterMap.get(FilterId.SKILL_TAXONOMY) as
        | SingleValueFilter<CustomSkillTaxonomySelection>
        | undefined;
      const activityFilter = activeFilterMap.get(FilterId.ACTIVITY_TAXONOMY) as
        | SingleValueFilter<CustomActivityTaxonomySelection>
        | undefined;
      const result: Partial<UserPreferences> = {};

      const taxonomiesResult: Partial<
        UserPreferences[UserPreferenceKey.Taxonomies]
      > = {};

      // An AI-applied taxonomy (`isAiApplied`) and one opened from a link
      // (`isLinkApplied`) are transient page state, not user preferences:
      // writing one to `taxonomies.role` would rewrite the saved preference
      // (an AI apply to 'adaptive', via the non-user-submitted inference
      // below). Skip only that write — still compute `rolePref` and, when it
      // resolves to adaptive, emit the view-only `effectiveRoleTaxonomySelection`
      // residue so the Taxonomies editor can name the taxonomy actually in
      // force ("Adaptive (<name>)") instead of falling back to a bare
      // "Adaptive" after an AI-paired apply. The residue is never persisted
      // (stripped in `preference-operations.ts` / `derive-effective-taxonomies.ts`),
      // so emitting it here cannot leak into the saved preference.
      if (roleFilter && roleFilter.value) {
        const value = roleFilter.value as CustomRoleTaxonomySelection;
        const isDefault = value?.default === true;
        const isRicsTaxonomy = !!(value?.ricsK50Id || value?.ricsK10Id);
        let rolePref: CustomRoleTaxonomySelection | 'adaptive' | undefined;

        const isCompanyAdaptiveMode =
          affectedScope === PreferenceScope.COMPANY &&
          !roleFilter.isUserSubmitted;

        // In COMPANY scope, "Adaptive" is represented by a non-user-submitted role taxonomy filter.
        // The effective value may still be the default taxonomy (fallback), so infer adaptive
        // before mapping explicit defaults.
        if (isCompanyAdaptiveMode && (isRicsTaxonomy || isDefault)) {
          rolePref = 'adaptive';
        } else {
          rolePref = isDefault ? { default: true } : value;
        }

        if (typeof rolePref !== 'undefined') {
          if (!roleFilter.isAiApplied && !roleFilter.isLinkApplied) {
            taxonomiesResult.role = rolePref;
          }
          if (rolePref === 'adaptive') {
            taxonomiesResult.effectiveRoleTaxonomySelection = value;
          }
        }
      }

      // Same rule as role above: an AI- or link-applied skill taxonomy must not
      // rewrite the stored preference, but the view-only effective-selection
      // residue should still be emitted so a skill-carrying AI apply keeps
      // its "Adaptive (<name>)" bracket too.
      if (skillFilter && skillFilter.value) {
        const valueObj = skillFilter.value as CustomSkillTaxonomySelection;
        const value = valueObj?.taxonomyId ?? 0;
        const isDefault = value === 0;

        let inferred: number | 'adaptive';
        if (!skillFilter.isUserSubmitted && affectedScope === 'COMPANY') {
          inferred = 'adaptive';
        } else if (isDefault) {
          inferred = 0;
        } else {
          inferred = value;
        }

        if (!skillFilter.isAiApplied && !skillFilter.isLinkApplied) {
          taxonomiesResult.skill = inferred;
        }
        if (inferred === 'adaptive') {
          taxonomiesResult.effectiveSkillTaxonomySelection = value;
        }
      }

      if (activityFilter?.value) {
        const value = activityFilter.value.taxonomyId ?? 0;

        let inferred: number | 'adaptive';
        if (
          !activityFilter.isUserSubmitted &&
          affectedScope === PreferenceScope.COMPANY
        ) {
          inferred = 'adaptive';
        } else {
          inferred = value;
        }

        if (!activityFilter.isAiApplied && !activityFilter.isLinkApplied) {
          taxonomiesResult.activity = inferred;
        }
        if (inferred === 'adaptive') {
          taxonomiesResult.effectiveActivityTaxonomySelection = value;
        }
      }

      if (Object.keys(taxonomiesResult).length > 0) {
        result[UserPreferenceKey.Taxonomies] = taxonomiesResult;
      }

      return result;
    },
    toFilter: (value, subKey) => {
      const updates: FilterUpdate[] = [];
      const composite = value as UserPreferences[UserPreferenceKey.Taxonomies];

      const shouldUpdateRole =
        !subKey || subKey === UserPreferenceSubKey.RoleTaxonomy;
      const shouldUpdateSkill =
        !subKey || subKey === UserPreferenceSubKey.SkillTaxonomy;
      const shouldUpdateActivity =
        !subKey || subKey === UserPreferenceSubKey.ActivityTaxonomy;

      const roleValue = composite?.role;
      if (shouldUpdateRole && typeof roleValue !== 'undefined') {
        if (roleValue === 'adaptive') {
          // "Adaptive" is a non-user-submitted, value-less filter: the reducer's
          // `deriveAdaptiveTaxonomies` re-resolves it from the company selection,
          // and the empty value leaves dependent ROLE selections intact.
          updates.push({
            id: FilterId.ROLE_TAXONOMY,
            isUserSubmitted: false,
          });
        } else {
          updates.push({
            id: FilterId.ROLE_TAXONOMY,
            type: FilterType.VALUE,
            value: roleValue as CustomRoleTaxonomySelection,
            isUserSubmitted: true,
          });
        }
      }

      const skillValue = composite?.skill;
      if (shouldUpdateSkill && typeof skillValue !== 'undefined') {
        if (skillValue === 'adaptive') {
          // See the role taxonomy note above: "Adaptive" is a non-user-submitted
          // filter, re-resolved by the reducer's `deriveAdaptiveTaxonomies`.
          updates.push({
            id: FilterId.SKILL_TAXONOMY,
            isUserSubmitted: false,
          });
        } else {
          updates.push({
            id: FilterId.SKILL_TAXONOMY,
            type: FilterType.VALUE,
            value: { taxonomyId: skillValue } as CustomSkillTaxonomySelection,
            isUserSubmitted: true,
          });
        }
      }

      const activityValue = composite?.activity;
      if (shouldUpdateActivity && typeof activityValue !== 'undefined') {
        if (activityValue === 'adaptive') {
          updates.push({
            id: FilterId.ACTIVITY_TAXONOMY,
            isUserSubmitted: false,
          });
        } else {
          updates.push({
            id: FilterId.ACTIVITY_TAXONOMY,
            type: FilterType.VALUE,
            value: {
              taxonomyId: activityValue,
            } as CustomActivityTaxonomySelection,
            isUserSubmitted: true,
          });
        }
      }

      return updates;
    },
  },
  [UserPreferenceKey.DataAdjustments]: {
    relevantFilters: [FilterId.DATA_ADJUSTMENTS],
    toPreference: (activeFilterMap) => {
      const filter = activeFilterMap.get(FilterId.DATA_ADJUSTMENTS);
      const selections = (filter as StaticListFilter)?.selections ?? [];
      if (!selections.length) {
        return {};
      }
      if (selections.some((s) => s.id === 'true')) {
        return { [UserPreferenceKey.DataAdjustments]: true };
      }
      return { [UserPreferenceKey.DataAdjustments]: false };
    },
    toFilter: (value) => [
      {
        id: FilterId.DATA_ADJUSTMENTS,
        type: FilterType.STATIC_LIST,
        selections: value
          ? [{ id: 'true', name: 'Unadjusted' }]
          : [{ id: '', name: 'Adjusted' }],
        isUserSubmitted: true,
        isVisible: false,
      },
    ],
  },
};

export const filtersToPreferences = ({
  activeFilterMap,
  affectedScope,
}: {
  activeFilterMap: FilterSelectionsMap;
  affectedScope: PreferenceScope;
}): Partial<UserPreferences> => {
  const result: Partial<UserPreferences> = {};
  (Object.keys(preferenceAdapters) as UserPreferenceKey[]).forEach((key) => {
    const adapter = preferenceAdapters[key];
    if (!adapter) return;
    Object.assign(result, adapter.toPreference(activeFilterMap, affectedScope));
  });
  return result;
};

export const buildFilterUpdatesFromPreferences = (
  preferences: Partial<UserPreferences>,
  subKey?: UserPreferenceSubKey
): FilterUpdate[] => {
  const updates: FilterUpdate[] = [];
  (Object.keys(preferences) as UserPreferenceKey[]).forEach((key) => {
    const value = preferences[key as keyof UserPreferences];
    const adapter = preferenceAdapters[key];
    if (!adapter || typeof value === 'undefined' || !adapter.toFilter) {
      return;
    }
    updates.push(...adapter.toFilter(value, subKey));
  });
  return updates;
};
